import { useMutation, useQueryClient } from '@tanstack/react-query';

import { verifyLineChannelWebhook } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TLineWebhookVerifyResponse } from 'types';
import { toErrorMessage } from 'hooks/utils';

type UseVerifyLineChannelWebhookOptions = {
  onSuccess?: (
    response: TLineWebhookVerifyResponse,
    lineChannelId: number,
  ) => void;
};

type UseVerifyLineChannelWebhookResult = {
  verify: (lineChannelId: number) => void;
  isSubmitting: boolean;
  error: string | undefined;
  resetError: () => void;
};

export function useVerifyLineChannelWebhook(
  options: UseVerifyLineChannelWebhookOptions = {},
): UseVerifyLineChannelWebhookResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = lineStrings.management;

  const mutation = useMutation({
    mutationFn: async (lineChannelId: number) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return verifyLineChannelWebhook(lineChannelId);
    },
    onSuccess: (response, lineChannelId) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS, user?.uid],
      });
      options.onSuccess?.(response, lineChannelId);
    },
  });

  const verify = (lineChannelId: number) => {
    mutation.mutate(lineChannelId);
  };

  const error = mutation.error
    ? toErrorMessage(mutation.error, strings.errors.verifyFailed)
    : undefined;

  return {
    verify,
    isSubmitting: mutation.isPending,
    error,
    resetError: mutation.reset,
  };
}
